import { Grid, Typography } from '@mui/material'
import React, { useMemo } from 'react'

// Common Components
import TableTemplate, { Column } from '../../Common/Table/TableTemplate'

// Redux
import { useAppSelector } from '../../../redux/store'

// Task Components
import TaskBadge from './TaskTableComponents/TaskBadge'
import TaskDueDateFormat from './TaskTableComponents/TaskDueDateFormat'
import TaskTitle from './TaskTableComponents/TaskTitle'
import TaskActions from './TaskActions/TaskActions'
import { Task } from '../../../redux/task/types'
import { selectLoadingFetchTasks } from '../../../redux/task/selector'

interface TaskTableProps {
  data: Task[]
  refreshTable: boolean
}

const TaskTable: React.FC<TaskTableProps> = ({ data, refreshTable }) => {
  const loading = useAppSelector(selectLoadingFetchTasks)
  const error = useAppSelector((state) => state.task.errorFetchTasks)
  const state = { loading, error }

  // Sort data by created date descending (most recent first)
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const createdDateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const createdDateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return createdDateB - createdDateA // Descending order (most recent first)
    })
  }, [data])

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'title',
        headerName: 'TITLE',
        headerAlign: 'center',
        bodyAlignment: 'left',
        sortable: true,
        width: '200px',
        renderCell: (_params: any, row: Task) => (
          <div 
            style={{ display: 'flex', alignItems: 'center', paddingLeft: '40px', paddingRight: '8px', width: '100%' }}
            title={row.title}
          >
            <TaskTitle value={row.title} />
          </div>
        ),
      },
      {
        field: 'details',
        headerName: 'DESCRIPTION',
        headerAlign: 'center',
        bodyAlignment: 'left',
        sortable: false,
        width: '250px',
        renderCell: (_params: any, row: Task) => (
          <div 
            style={{ display: 'flex', alignItems: 'center', paddingLeft: '16px', paddingRight: '8px', width: '100%' }}
            title={row.details || 'No description'}
          >
            <Typography
              sx={{
                maxWidth: '100%',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                textAlign: 'left',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: 0,
                fontSize: '12px',
                fontWeight: 400,
                flex: 1,
                lineHeight: 1.4,
                maxHeight: '50.4px', // 3 lines * 16.8px line height (12px * 1.4)
              }}
            >
              {row.details || '-'}
            </Typography>
          </div>
        ),
      },
      {
        field: 'dueDate',
        headerName: 'DUE DATE',
        headerAlign: 'center',
        bodyAlignment: 'center',
        sortable: true,
        width: '150px',
        renderCell: (_params: any, row: Task) => (
          <TaskDueDateFormat value={row.dueDate?.toString() || ''} />
        ),
      },
      {
        field: 'importance',
        headerName: 'IMPORTANCE',
        headerAlign: 'center',
        bodyAlignment: 'center',
        sortable: false,
        width: '120px',
        renderCell: (_params: any, row: Task) => (
          <Grid 
            container 
            justifyContent='center' 
            alignItems='center'
            data-testid={`manage-task-importance-cell-${row.id}`}
          >
            <TaskBadge value={row.importance} badgeType='importanceBadge' />
          </Grid>
        ),
      },
      {
        field: 'action',
        headerName: 'ACTION',
        headerAlign: 'center',
        bodyAlignment: 'center',
        width: '180px',
        renderCell: (_params: any, row: Task) => {
          return <TaskActions taskData={row} />
        },
      },
    ],
    [refreshTable],
  )

  return (
    <Grid data-testid="manage-task-table">
      <TableTemplate
        columns={columns}
        datas={sortedData}
        state={state}
        refreshTable={refreshTable}
        uniqueField='id'
      />
    </Grid>
  )
}

export default TaskTable
